/**
 * VqeFilterChipBuilder — the VQE-Edit WHERE-filter chip builder. Reuses the
 * Table Explorer's column-scoped `DraftFilterChip` for value editing and the
 * SELECT list's `CommandPickerPopover` for (multi-table) column selection.
 *
 * `FilterWizard.tsx` / `FilterChip.tsx` are NOT modified — they're shared with
 * Table Explorer. `DraftFilterChip` takes ONE already-chosen column + its
 * single-table context; multi-table is this wrapper's job (pick column → resolve
 * owning table via `useVqeFilterColumns` → feed the chip that column + context).
 *
 * Two modes:
 *  - `add`  — trigger → column picker → chip → on commit, lower to predicate
 *             text and append via `addFilter`.
 *  - `edit` — seed the chip from an existing predicate (`predicateToFilterCondition`)
 *             and re-emit on commit via `onCommit(text)`. When the predicate
 *             can't be parsed, `onUnresolvable` fires so the caller (Task 14)
 *             can fall back to a raw text input.
 *
 * PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/modify/remove
 * filter (E4, acceptance #5/#9).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import type { FilterCondition, FilterGroup } from '../../../core/types/workspaceTypes';
import type { DataTypeCategory } from '../../../common/utils/filterUtils';
import { DraftFilterChip } from '../../../common/components/filters/FilterChip';
import { useCellFilterDraftRequest } from '../../../common/hooks/useCellFilterDraftRequest';
import { buildConditionSql } from '../../../common/utils/filterWhereBuilder';
import type { WhereCombinator } from '../../../../sql-resolver/types';
import type { BooleanClauseTarget } from '../../../../sql-resolver/types/structuralEdit';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { addFilter } from '../../edit/edits/addFilter';
import { sqlEngineToDatabaseEngineId } from '../../edit/livePullSchemaSnapshot';
import { useVqeStore } from '../../store/vqeStore';
import { vqeCopy } from '../../ux/vqeCopy';
import {
  buildAggregateResolvedColumn,
  buildAggregatesGroup,
  findAggregateTemplate,
  type AggregateFnName,
} from './AggregateFieldTemplates';
import { CommandPickerPopover, type CommandPickerGroup } from './CommandPickerPopover';
import { enrichParsedCondition, resolveEditPredicate } from './looseFkEditEnrichment';
import { committedPredicateSql } from '../../../common/utils/predicateToFilterCondition';
import { useLiveFilterPick } from './useLiveFilterPick';
import { useVqeChipDataScope } from './useVqeChipDataScope';
import { useVqeFilterColumns, type ResolvedFilterColumn } from './useVqeFilterColumns';

type AddProps = {
  readonly mode: 'add';
  readonly testId: string;
  readonly triggerLabel: string;
  readonly editContext: VqeEditContext;
  readonly combinator: WhereCombinator;
  /**
   * When true, the picker shows an AND/OR connector toggle in its header and
   * the chosen connector (not the fixed `combinator` prop, which becomes the
   * initial value) drives the commit. Used by the single top-level "+ Add"
   * footer and the per-leaf "convert to group" affordance.
   */
  readonly pickConnector?: boolean;
  /**
   * When provided, called with the lowered predicate text + the effective
   * connector instead of invoking `addFilter` directly. The caller is
   * responsible for persisting the edit. Used by `RailAddCondition` (routes to
   * `addFilterNode`) and the leaf-convert affordance (routes to `groupLeaf`).
   */
  readonly onCommit?: (predicateText: string, combinator: WhereCombinator) => void;
  /**
   * Target clause this chip serves — `'where'` (default, absent) or
   * `'having'`. Selects the field-step template group (`'having'` leads with
   * the Aggregates templates); the default `addFilter` commit path also
   * targets it. Commit routing is caller-owned when `onCommit` is supplied —
   * `clause` still drives the field-step content there.
   */
  readonly clause?: BooleanClauseTarget;
  /**
   * Fires when the draft chip opens (a column was picked) and when it closes
   * (applied / cancelled). The empty WHERE / HAVING card hides its "No filters
   * yet" sentence while a draft is open.
   */
  readonly onDraftOpenChange?: (open: boolean) => void;
  /** Trigger look — the default ghost "+ Add" link, or the small condition chip. */
  readonly triggerVariant?: 'ghost' | 'chip';
  /** Tooltip / accessible name for a short trigger label. */
  readonly hint?: string;
};
type EditProps = {
  readonly mode: 'edit';
  readonly testId: string;
  readonly editContext: VqeEditContext;
  readonly initialPredicate: string;
  readonly onCommit: (predicateText: string) => void;
  readonly onCancel?: () => void;
  /**
   * Fires when `initialPredicate` can't be parsed into an editable condition
   * (functions, subqueries, multi-predicate text the chip did not write).
   * Task 14 uses it to fall back to a raw text input. When this fires the
   * builder renders nothing.
   */
  readonly onUnresolvable?: () => void;
};
export type VqeFilterChipBuilderProps = AddProps | EditProps;

/** Value-input placeholder by type: "Number" / "Text", else "Value". */
function vqeValuePlaceholder(category: DataTypeCategory): string {
  if (category === 'number') return vqeCopy.placeholder.filterValueNumber;
  if (category === 'string') return vqeCopy.placeholder.filterValueText;
  return vqeCopy.placeholder.filterValue;
}

/**
 * Value-based equality for a parsed condition value (`string | number |
 * boolean | Date | null | string[] | undefined`). Compares arrays element-wise
 * and Dates by timestamp so a structurally-identical value produced by a fresh
 * parse compares equal even though its object identity churned.
 */
function sameConditionValue(a: FilterCondition['value'], b: FilterCondition['value']): boolean {
  if (a === b) return true;
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }
  return false;
}

/**
 * Structural (by-value) equality for two `FilterCondition`s. Used by the
 * edit-mode re-sync effect so an upstream identity churn (the resolver
 * `picker`/`resolveByField` re-identifying, or MetaInfo/pg-version settling)
 * does NOT force a `setDraft` when the actual condition is unchanged. A purely
 * referential compare here spun an infinite re-render loop, because every
 * render produced a freshly-parsed (new-identity) condition.
 */
function sameFilterCondition(a: FilterCondition | undefined, b: FilterCondition | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.operator === b.operator &&
    a.value2 === b.value2 &&
    a.jsonSubOperator === b.jsonSubOperator &&
    a.path === b.path &&
    a.jsonDeclaredOnText === b.jsonDeclaredOnText &&
    a.sourcePath === b.sourcePath &&
    sameConditionValue(a.value, b.value)
  );
}

/**
 * Telemetry for low-cardinality operator kinds (no column/value names).
 * The `json` branch fires the SAME `feature:json_filter_apply` event Table
 * Explorer fires for a JSON filter apply (`FilterWizard.tsx` / `FilterChip.tsx`),
 * with the same low-cardinality properties, so the two surfaces are counted
 * together rather than the VQE apply going unrecorded (report G7).
 */
function trackOperator(
  track: ReturnType<typeof useAnalytics>['track'],
  condition: Pick<FilterCondition, 'operator' | 'jsonSubOperator' | 'path'>,
  engine: DatabaseEngineId,
): void {
  if (condition.operator === 'enum') track('feature:vqe_filter_enum', { engine });
  else if (condition.operator === 'lookup') track('feature:vqe_filter_lookup', { engine });
  else if (condition.operator === 'json') {
    track('feature:json_filter_apply', {
      engine,
      sub_operator: condition.jsonSubOperator ?? 'has_property',
      has_path: !!condition.path,
    });
  }
}

export function VqeFilterChipBuilder(props: VqeFilterChipBuilderProps) {
  // `mode` is stable per mounted instance, so the branch picks one component
  // type consistently — no hook-order hazard.
  return props.mode === 'add' ? <AddBuilder {...props} /> : <EditBuilder {...props} />;
}

/**
 * AND/OR connector toggle rendered in the picker header (Round 8). Lets the
 * single "+ Add" affordance choose how the new predicate joins. Buttons
 * suppress the default mousedown so the popover's focus-outside guard doesn't
 * tear the picker down mid-selection (mirrors the scope toggle).
 */
function ConnectorToggle({
  testId,
  value,
  onChange,
}: {
  readonly testId: string;
  readonly value: WhereCombinator;
  readonly onChange: (c: WhereCombinator) => void;
}) {
  return (
    <div
      className="flex items-center gap-2 border-b border-border px-2 py-1.5"
      data-testid={`${testId}-connector-toggle`}
    >
      <span className="text-[11px] text-muted-foreground">Join with</span>
      <span className="inline-flex gap-0.5 rounded-md border border-border bg-background p-0.5">
        {(['AND', 'OR'] as const).map((c) => {
          const active = value === c;
          return (
            <button
              key={c}
              type="button"
              data-testid={`${testId}-connector-${c.toLowerCase()}`}
              data-active={active ? 'true' : 'false'}
              aria-pressed={active}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onChange(c)}
              className={
                active
                  ? 'rounded px-2 py-0.5 text-[11px] font-semibold text-foreground bg-muted'
                  : 'rounded px-2 py-0.5 text-[11px] font-semibold text-muted-foreground hover:text-foreground'
              }
            >
              {c}
            </button>
          );
        })}
      </span>
    </div>
  );
}

function AddBuilder({
  testId,
  triggerLabel,
  editContext,
  combinator,
  onCommit,
  pickConnector,
  clause,
  onDraftOpenChange,
  triggerVariant,
  hint,
}: AddProps) {
  const { track } = useAnalytics();
  const cols = useVqeFilterColumns(editContext);
  const [picked, setPicked] = useState<ResolvedFilterColumn | null>(null);
  const [draft, setDraft] = useState<FilterGroup | null>(null);
  // Picker key of a plain column pick, so the open draft follows MetaInfo /
  // snapshot refreshes (`useLiveFilterPick`). Null for aggregate templates.
  const [pickedKey, setPickedKey] = useState<string | null>(null);
  useLiveFilterPick({ resolve: cols.resolve, pickedKey, picked, setPicked, setDraft });
  // Set once the user picks a parameterized aggregate template (SUM/AVG/…) —
  // the field step then re-shows the standard column list for the argument.
  // `COUNT(*)` (zero-arg) never sets this; it commits directly.
  const [pendingAggFn, setPendingAggFn] = useState<AggregateFnName | null>(null);
  // When `pickConnector`, the header toggle drives the connector (seeded from
  // the `combinator` prop); otherwise the fixed prop is used verbatim.
  const [connector, setConnector] = useState<WhereCombinator>(combinator);
  const effectiveCombinator = pickConnector ? connector : combinator;

  const draftOpen = !!(draft && picked);
  const scope = useVqeChipDataScope(editContext, cols, draftOpen ? picked : null);
  useEffect(() => {
    onDraftOpenChange?.(draftOpen);
  }, [draftOpen, onDraftOpenChange]);

  const reset = useCallback(() => {
    setPicked(null);
    setPickedKey(null);
    setDraft(null);
    setPendingAggFn(null);
  }, []);

  const commitResolved = useCallback((resolved: ResolvedFilterColumn, key: string | null = null) => {
    setPicked(resolved);
    setPickedKey(key);
    setDraft(resolved.draft);
    setPendingAggFn(null);
  }, []);

  // The results grid's "Add to WHERE" in the Visual view opens its cell here as a pre-filled draft.
  useCellFilterDraftRequest(onCommit || clause === 'having' ? null : editContext, cols, commitResolved);

  const handlePick = useCallback(
    (key: string) => {
      // Aggregates group is only offered on the HAVING field step (decision 4).
      const template = clause === 'having' ? findAggregateTemplate(key) : undefined;
      if (template) {
        if (template.zeroArg) {
          commitResolved(buildAggregateResolvedColumn(template.fn, null));
        } else {
          // Parameterized — the next pick (`handlePickAggregateArg`) supplies
          // the argument column from the SAME standard column list.
          setPendingAggFn(template.fn);
        }
        return;
      }
      const resolved = cols.resolve(key);
      if (!resolved) return;
      commitResolved(resolved, key);
    },
    [cols, clause, commitResolved],
  );

  const handlePickAggregateArg = useCallback(
    (key: string) => {
      if (!pendingAggFn) return;
      const argument = cols.resolve(key);
      if (!argument) return;
      commitResolved(buildAggregateResolvedColumn(pendingAggFn, argument));
    },
    [cols, pendingAggFn, commitResolved],
  );

  // A parameterized aggregate template (SUM/AVG/…) picked without a follow-up
  // argument pick left `pendingAggFn` stuck once the picker was dismissed
  // (Escape / outside click) — reopening skipped the Aggregates group with no
  // way back short of picking an unwanted column. `picked` stays null on that
  // dismiss path: a completed pick sets it via `commitResolved` in the SAME
  // event that flips the component to the `<DraftFilterChip>` branch, which
  // unmounts this popover (and this handler) before Radix could ever fire
  // `onOpenChange(false)` for it — so the `!picked` guard only ever matters
  // for the true "closed without picking" case.
  const handlePickerOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !picked) setPendingAggFn(null);
    },
    [picked],
  );

  const handleCommit = useCallback(
    (override?: FilterGroup) => {
      const committed = override ?? draft;
      const condition = committed?.conditions[0];
      if (!committed || !picked || !condition) return;
      // `valueDataType` — a date / timestamp column's (or MIN/MAX argument's)
      // values are written as the engine's temporal literal, like Table
      // Explorer's generators (`sqlTemporalLiteral`).
      const predicate = buildConditionSql(picked.quotedField, condition, cols.engine, {
        quoteSiblingField: picked.quoteSiblingField,
        valueDataType: picked.column.dataType,
      });
      reset();
      if (!predicate) return; // inactive (e.g. empty enum multiselect)
      if (onCommit) {
        onCommit(predicate, effectiveCombinator);
        trackOperator(track, condition, cols.engine);
        return;
      }
      // Only add the `clause` key when it targets HAVING — keeps the WHERE
      // (default/absent) call shape byte-identical to before this option existed.
      const opts = clause === 'having'
        ? { combinator: effectiveCombinator, clause: 'having' as const }
        : { combinator: effectiveCombinator };
      // Success analytics is OUTCOME-DEPENDENT: `commitEdit` no longer rejects on
      // a commit failure (P12 §4.2), so branch on the `CommitOutcome` — fire the
      // add-filter events only when the edit actually reached the buffer. The
      // `.catch` now covers ONLY `addFilter`'s wrapper pre-validation throw
      // (empty predicate) — a commit-pipeline failure already recorded its own
      // feedback toast inside commitEdit.
      addFilter(predicate, toCommitCtx(editContext), opts)
        .then((outcome) => {
          if (outcome.status !== 'applied') return;
          track('feature:vqe_add_filter', { combinator: effectiveCombinator === 'OR' ? 'or' : 'and' });
          trackOperator(track, condition, cols.engine);
        })
        .catch((err) => {
          // Wrapper pre-validation rejected before commitEdit — surface it as a
          // toast so users see an error instead of a chip that vanishes with no
          // SQL change.
          useVqeStore.getState().recordCommitFeedback({
            kind: 'failure',
            at: Date.now(),
            message: err instanceof Error ? err.message : String(err),
          });
        });
    },
    [draft, picked, cols.engine, editContext, effectiveCombinator, track, reset, onCommit, clause],
  );

  if (draft && picked) {
    return (
      <DraftFilterChip
        filterGroup={draft}
        column={picked.column}
        onUpdate={setDraft}
        onCommit={handleCommit}
        onCancel={reset}
        connectionId={cols.connectionId}
        database={cols.database}
        tableSchema={picked.schema}
        tableName={picked.table}
        jsonContext={picked.jsonContext}
        sampleRows={scope.sampleRows}
        scanFilters={scope.scanFilters}
        valuePlaceholder={vqeValuePlaceholder}
        applyStyle="primary"
      />
    );
  }

  // Aggregates group leads the HAVING field step (decision 4); WHERE stays
  // pixel-identical to before this option existed. Once a parameterized
  // template is pending, the popover re-shows the standard column list (no
  // Aggregates group) so the SAME picker serves the argument pick.
  const groups: readonly CommandPickerGroup[] =
    clause === 'having' && !pendingAggFn
      ? [buildAggregatesGroup(), ...cols.picker.groups]
      : cols.picker.groups;

  return (
    <CommandPickerPopover
      testId={testId}
      triggerLabel={triggerLabel}
      triggerVariant={triggerVariant}
      hint={hint}
      disabled={!cols.picker.enabled}
      tooltip={
        cols.picker.enabled
          ? pickConnector
            ? 'Add a filter (choose AND / OR)'
            : `Add ${combinator} filter`
          : vqeCopy.disabled.filterEditingUnavailable
      }
      searchPlaceholder="Search columns…"
      groups={groups}
      onSelect={cols.picker.enabled ? (pendingAggFn ? handlePickAggregateArg : handlePick) : undefined}
      onOpenChange={handlePickerOpenChange}
      scopeToggle={cols.picker.scopeToggle}
      loading={cols.picker.isLoading}
      emptyMessage={cols.picker.emptyMessage}
      notice={cols.picker.notice}
      header={
        pickConnector ? (
          <ConnectorToggle testId={testId} value={connector} onChange={setConnector} />
        ) : undefined
      }
    />
  );
}

function EditBuilder({ testId, editContext, initialPredicate, onCommit, onCancel, onUnresolvable }: EditProps) {
  const { track } = useAnalytics();
  const engine = sqlEngineToDatabaseEngineId(editContext.service.engineId);
  // useVqeFilterColumns must be called unconditionally (it's a hook) — before
  // any early-return guards.
  const cols = useVqeFilterColumns(editContext);
  // PARITY-bundle review fix: `resolveEditPredicate` is the SINGLE shared
  // "can this predicate resolve into a structured chip" check — the generic
  // parser, then (PG-only) the `enrichPgJsonLookupFromRaw` raw-extract
  // loose-FK fallback (see its header in `looseFkEditEnrichment.ts`) when the
  // generic parse comes back empty. `FilterEditablePill.tsx` and
  // `RailLeafChip.tsx`'s editing body gate on this SAME function, so their
  // "chip-eligible" decision can never drift from what this builder can
  // actually resolve.
  const resolution = useMemo(
    () => resolveEditPredicate(initialPredicate, engine, cols.resolveByField),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialPredicate, engine, cols.resolveByField],
  );
  const parsed = resolution?.parsed ?? null;
  const resolved = resolution?.resolved ?? null;
  const scope = useVqeChipDataScope(editContext, cols, resolved, initialPredicate);

  // The condition as the column's chip shows it — boolean switch, enum,
  // lookup, JSON enum / lookup picks — when that writes the same SQL (see
  // `looseFkEditEnrichment.ts`); otherwise `parsed` unchanged.
  const enrichedParsed = useMemo(
    () => (parsed && resolved ? enrichParsedCondition(parsed, resolved, engine) : parsed),
    [parsed, resolved, engine],
  );

  // `draft`'s seed can't be a plain `useState` lazy-initializer keyed off
  // `enrichedParsed`: `resolved` — and therefore the loose-FK enrichment,
  // and (for the PG fallback shape) `parsed` itself — depends on
  // `cols.resolveByField`, which resolves against the in-scope column
  // picker's data. That data is populated via the picker's OWN internal
  // effect (not available synchronously on `EditBuilder`'s first render even
  // when the schema snapshot was set before mount), so a lazy initializer —
  // which only ever runs once — can lock in a stale (un-enriched, or even
  // null) result before the picker/MetaInfo catch up a render or two later.
  //
  // Fix: keep RE-SYNCING `draft` from `enrichedParsed` on every render where
  // they've drifted apart, until the user's first actual edit (tracked by
  // `userEditedRef`, flipped only by `handleDraftUpdate` below — never by
  // this effect). This converges to the correct seed regardless of how many
  // renders the picker/MetaInfo take to settle, without ever clobbering an
  // in-flight edit on a later MetaInfo refetch.
  //
  // The draft's `field` is the chip's MetaInfo identity — `DraftFilterChip`
  // keys loose-FK / target-filter / JSON-source-path lookups by it — so it is
  // the BARE column name once the field resolves (`account_id`), never the
  // predicate's SQL text (`b.account_id` / `orders."cust_id"`), which matches
  // no declaration and made the lookup open "declare a relationship" whose
  // save then wrote a qualified `fromColumn`. `parsed.field` stays the
  // re-emit form (`handleCommit` below). An unresolved field keeps its SQL
  // text as a label only — `tableName=""` below suppresses the lookup.
  const identityField = resolved ? resolved.column.name : enrichedParsed?.field ?? null;
  const [draft, setDraft] = useState<FilterGroup | null>(
    enrichedParsed && identityField !== null
      ? { field: identityField, conditions: [enrichedParsed.condition] }
      : null,
  );
  const userEditedRef = useRef(false);
  useEffect(() => {
    if (!enrichedParsed || identityField === null) return;
    setDraft((prev) => {
      // After the user's first edit only the identity may still catch up (the
      // column resolving a render late) — their condition is never replaced.
      if (userEditedRef.current) {
        return !prev || prev.field === identityField ? prev : { ...prev, field: identityField };
      }
      // Compare by VALUE, not by reference. `enrichedParsed.condition` is a
      // freshly-parsed object on every render, so a referential `===` here was
      // ALWAYS false once any upstream identity churned — re-firing `setDraft`
      // and spinning an infinite re-render loop. A structural compare keeps the
      // draft stable whenever the resolved condition is unchanged.
      if (prev && prev.field === identityField && sameFilterCondition(prev.conditions[0], enrichedParsed.condition)) {
        return prev; // already in sync — avoid a redundant re-render
      }
      return { field: identityField, conditions: [enrichedParsed.condition] };
    });
  }, [enrichedParsed, identityField]);
  const handleDraftUpdate = useCallback((next: FilterGroup) => {
    userEditedRef.current = true;
    setDraft(next);
  }, []);

  // Same drift hazard as `draft` above — `parsed` (via the PG loose-FK
  // fallback) can flip from `null` to resolved a render or two after the
  // picker/MetaInfo settle. Deferring on `cols.picker.isLoading` (the
  // column-picker's own readiness signal — `resolveByField`'s data source)
  // means we only ever declare a predicate unresolvable once the picker has
  // actually had a chance to resolve its field, so a predicate that WILL
  // eventually resolve never spuriously reports unresolvable.
  const firedRef = useRef(false);
  useEffect(() => {
    if (cols.picker.isLoading || parsed || firedRef.current) return;
    firedRef.current = true;
    onUnresolvable?.();
  }, [cols.picker.isLoading, parsed, onUnresolvable]);

  const handleCommit = useCallback(
    (override?: FilterGroup) => {
      const committed = override ?? draft;
      const condition = committed?.conditions[0];
      if (!committed || !parsed || !condition) return;
      // Unchanged: the predicate as written. Else re-emit with its own field
      // text and literal types (`committedPredicateSql`; FIX-F: an untyped
      // input's `3` stays `n < 3`). `parsed` (not `enrichedParsed`) is the
      // correct baseline here — the enrichment never changes `.field`/`.value`,
      // only `.operator` / `.sourcePath`, and `parsed` is available a render earlier.
      const predicate = committedPredicateSql(initialPredicate, parsed, condition, engine, {
        quoteSiblingField: resolved?.quoteSiblingField,
        columnDataType: resolved?.column.dataType,
      });
      if (!predicate) return;
      onCommit(predicate);
      trackOperator(track, condition, engine);
    },
    [draft, parsed, resolved, engine, onCommit, track, initialPredicate],
  );

  if (!draft || !parsed) return null;
  return (
    <span data-testid={testId} className="inline-flex">
      <DraftFilterChip
        filterGroup={draft}
        column={resolved?.column}
        onUpdate={handleDraftUpdate}
        onCommit={handleCommit}
        onCancel={onCancel ?? (() => {})}
        connectionId={cols.connectionId}
        database={cols.database}
        tableSchema={resolved?.schema}
        // `''` (not undefined) when the field resolves to no schema column —
        // the table-less sentinel that drops the `lookup` operator, which
        // would otherwise dead-end on a declaration for an expression.
        tableName={resolved ? resolved.table : ''}
        jsonContext={resolved?.jsonContext}
        sampleRows={scope.sampleRows}
        scanFilters={scope.scanFilters}
        valuePlaceholder={vqeValuePlaceholder}
        applyStyle="primary"
      />
    </span>
  );
}
